import styles from "../styles/Home.module.css";
import { useContext, useState, useEffect } from "react";
import DataContext from "../context/DataContext";
import Pearson2 from "../components/svgs/Pearson2";
import ArrowDown from "../components/svgs/ArrowDown";
import React from "react";
import json from "../context/Demo_Questions.json"
import { Check, Mic, Pause, Play } from "lucide-react"
import Cross from "../components/svgs/Cross";

const images = {};
function importAll(r) {
    r.keys().forEach((key) => (images[key] = r(key)));
}
importAll(require.context("../diagrams", false, /\.(png|jpe?g|svg)$/));

const specialSymbols = {
    '*': '×',
    '->': '→',
    '<-': '←',
    'arru': '↑',
    'arrd': '↓',
    '<->': '⇌',
    'alpha': 'α',
    'beta': 'β',
    'gamma': 'γ',
    'delta': 'δ',
    'epsilon': 'ε',
    'zeta': 'ζ',
    'eta': 'η',
    'theta': 'θ',
    'iota': 'ι',
    'kappa': 'κ',
    'lambda': 'λ',
    'mu': 'μ',
    'nu': 'ν',
    'xi': 'ξ',
    'omicron': 'ο',
    'pi': 'π',
    'rho': 'ρ',
    'sigma': 'σ',
    'tau': 'τ',
    'upsilon': 'υ',
    'phi': 'φ',
    'chi': 'χ',
    'psi': 'ψ',
    'omega': 'ω',
    'Alpha': 'Α',
    'Beta': 'Β',
    'Gamma': 'Γ',
    'Delta': 'Δ',
    'Epsilon': 'Ε',
    'Zeta': 'Ζ',
    'Eta': 'Η',
    'Theta': 'Θ',
    'Iota': 'Ι',
    'Kappa': 'Κ',
    'Lambda': 'Λ',
    'Mu': 'Μ',
    'Nu': 'Ν',
    'Xi': 'Ξ',
    'Omicron': 'Ο',
    'Pi': 'Π',
    'Rho': 'Ρ',
    'Sigma': 'Σ',
    'Tau': 'Τ',
    'Upsilon': 'Υ',
    'Phi': 'Φ',
    'Chi': 'Χ',
    'Psi': 'Ψ',
    'Omega': 'Ω',
    'degree': '°',
    'tick': '✓',
    'standardconditions': '⦵',
    // Add any other Greek letters or special symbols here...
};

const symbols = [
    { symbol: '*', replacement: '×' },
    { symbol: '==', replacement: '≡' },
];

const getLetterFromNumber = (number) => {
    const baseCharCode = 'a'.charCodeAt(0);
    const targetCharCode = baseCharCode + number;
    return String.fromCharCode(targetCharCode);
};

function LatexFormatter({ text, fractionSize = '1.5625rem', fontSize = "1.3rem", fontFamily = "Space Grotesk", fontWeight = 500 }) {
    const formatText = (str, currentFractionSize) => {
        if (!str) return [];

        for (const { symbol, replacement } of symbols) {
            str = str.replace(new RegExp(escapeRegExp(symbol), 'g'), replacement);
        }

        const subscriptPattern = /\_\{([^{}]*?(?:\{[^{}]*?\}[^{}]*?)*)\}/;
        const superscriptPattern = /\^\{([^{}]*?(?:\{[^{}]*?\}[^{}]*?)*)\}/;
        const fractionPattern = /\\frac\{([^{}]*?(?:\{[^{}]*?\}[^{}]*?)*)\}\{([^{}]*?(?:\{[^{}]*?\}[^{}]*?)*)\}/;
        const symbolPattern = /\{([^{}]*?(?:\{[^{}]*?\}[^{}]*?)*)\}/;
        const chemPattern = /\\chem\{([^{}]*?(?:\{[^{}]*?\}[^{}]*?)*)\}\{([^{}]*?(?:\{[^{}]*?\}[^{}]*?)*)\}\{([^{}]*?(?:\{[^{}]*?\}[^{}]*?)*)\}\{([^{}]*?(?:\{[^{}]*?\}[^{}]*?)*)\}\{([^{}]*?(?:\{[^{}]*?\}[^{}]*?)*)\}/;
        const boldPattern = /\\bold\{([^{}]*?(?:\{[^{}]*?\}[^{}]*?)*)\}/;
        const italicPattern = /\\italic\{([^{}]*?(?:\{[^{}]*?\}[^{}]*?)*)\}/;
        const smallPattern = /\\small\{([^{}]*?(?:\{[^{}]*?\}[^{}]*?)*)\}/;
        const bigPattern = /\\big\{([^{}]*?(?:\{[^{}]*?\}[^{}]*?)*)\}/;
        const paddingPattern = /\\padding\{([0-9]*)\}/;
        const newLinePattern = /\\nl/;
        const imagePattern = /\\image\{([^{}]*?(?:\{[^{}]*?\}[^{}]*?)*)\}/;
        const inputPattern = /\\input\{([^{}]*?(?:\{[^{}]*?\}[^{}]*?)*)\}/;

        let matchSub = subscriptPattern.exec(str);
        let matchSup = superscriptPattern.exec(str);
        let matchFrac = fractionPattern.exec(str);
        let matchSym = symbolPattern.exec(str);
        let matchChem = chemPattern.exec(str);
        let matchBold = boldPattern.exec(str);
        let matchItalic = italicPattern.exec(str);
        let matchBig = bigPattern.exec(str);
        let matchSmall = smallPattern.exec(str);
        let matchNewLine = newLinePattern.exec(str);
        let matchPadding = paddingPattern.exec(str);
        let matchImage = imagePattern.exec(str);
        let matchInput = inputPattern.exec(str);

        // ... rest of the code that finds matches and performs replacements ...
        if (matchSub || matchSup || matchFrac || matchSym || matchChem || matchBold || matchItalic || matchBig || matchSmall || matchNewLine || matchPadding || matchImage) {
            let match;

            if (matchPadding && (!matchSub || matchPadding.index < matchSub.index) && (!matchSup || matchPadding.index < matchSup.index) && (!matchFrac || matchPadding.index < matchFrac.index) && (!matchSym || matchPadding.index < matchSym.index) && (!matchChem || matchPadding.index < matchChem.index) && (!matchBold || matchPadding.index < matchBold.index) && (!matchItalic || matchPadding.index < matchItalic.index) && (!matchSmall || matchPadding.index < matchSmall.index) && (!matchBig || matchPadding.index < matchBig.index)) {
                match = matchPadding;
                let paddingSize = parseInt(match[1], 10) * 40;
                return (
                    <React.Fragment>
                        {formatText(str.substring(0, match.index), currentFractionSize)}
                        <span style={{ paddingLeft: `${paddingSize}px` }}></span>
                        {formatText(str.substring(match.index + match[0].length), currentFractionSize)}
                    </React.Fragment>
                );
            };

            if (matchImage && (!matchSub || matchImage.index < matchSub.index) && (!matchSup || matchImage.index < matchSup.index) && (!matchFrac || matchImage.index < matchFrac.index) && (!matchSym || matchImage.index < matchSym.index) && (!matchChem || matchImage.index < matchChem.index) && (!matchBold || matchImage.index < matchBold.index) && (!matchItalic || matchImage.index < matchItalic.index) && (!matchSmall || matchImage.index < matchSmall.index) && (!matchBig || matchImage.index < matchBig.index)) {
                match = matchImage;
                return (
                    <React.Fragment>
                        {formatText(str.substring(0, match.index), currentFractionSize)}
                        <img style={{ maxWidth: "100px" }} src={images[`./${match[1]}.png`]} />
                        {formatText(str.substring(match.index + match[0].length), currentFractionSize)}
                    </React.Fragment>
                );
            };

            if (matchInput && (!matchSub || matchInput.index < matchSub.index) && (!matchSup || matchInput.index < matchSup.index) && (!matchFrac || matchInput.index < matchFrac.index) && (!matchSym || matchInput.index < matchSym.index) && (!matchChem || matchInput.index < matchChem.index) && (!matchBold || matchInput.index < matchBold.index) && (!matchItalic || matchInput.index < matchItalic.index) && (!matchSmall || matchInput.index < matchSmall.index) && (!matchBig || matchInput.index < matchBig.index)) {
                match = matchInput;
                return (
                    <React.Fragment>
                        {formatText(str.substring(0, match.index), currentFractionSize)}
                        <input style={{ width: "fit-content", display: "inline", height: "fit-content", minHeight: "0px", border: "none", borderBottom: "1px solid var(--secondary-color)" }} />
                        {formatText(str.substring(match.index + match[0].length), currentFractionSize)}
                    </React.Fragment>
                );
            };

            if (matchSub && (!matchSup || matchSub.index < matchSup.index) && (!matchFrac || matchSub.index < matchFrac.index) && (!matchSym || matchSub.index < matchSym.index) && (!matchChem || matchSub.index < matchChem.index) && (!matchBold || matchSub.index < matchBold.index) && (!matchItalic || matchSub.index < matchItalic.index) && (!matchSmall || matchSub.index < matchSmall.index) && (!matchBig || matchSub.index < matchBig.index) && (!matchNewLine || matchSub.index < matchNewLine.index) && (!matchPadding || matchSub.index < matchPadding.index)) {
                match = matchSub;
                return (
                    <React.Fragment>
                        {formatText(str.substring(0, match.index), currentFractionSize)}
                        <sub>{formatText(match[1], currentFractionSize)}</sub>
                        {formatText(str.substring(match.index + match[0].length), currentFractionSize)}
                    </React.Fragment>
                );
            } else if (matchSup && (!matchSub || matchSup.index < matchSub.index) && (!matchFrac || matchSup.index < matchFrac.index) && (!matchSym || matchSup.index < matchSym.index) && (!matchChem || matchSup.index < matchChem.index)) {
                match = matchSup;
                return (
                    <React.Fragment>
                        {formatText(str.substring(0, match.index), currentFractionSize)}
                        <sup style={{ position: 'relative', top: '-0em' }}>{formatText(match[1], currentFractionSize)}</sup>
                        {formatText(str.substring(match.index + match[0].length), currentFractionSize)}
                    </React.Fragment>
                );
            } else if (matchFrac && (!matchSub || matchFrac.index < matchSub.index) && (!matchSup || matchFrac.index < matchSup.index) && (!matchSym || matchFrac.index < matchSym.index) && (!matchChem || matchFrac.index < matchChem.index) && (!matchBold || matchFrac.index < matchBold.index) && (!matchItalic || matchFrac.index < matchItalic.index) && (!matchSmall || matchFrac.index < matchSmall.index) && (!matchBig || matchFrac.index < matchBig.index)) {
                match = matchFrac;
                let nextFractionSize = `calc(${currentFractionSize} * 0.8)`;
                return (
                    <React.Fragment>
                        {formatText(str.substring(0, match.index), currentFractionSize)}
                        <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', verticalAlign: 'middle' }}>
                            <span style={{ display: 'inline-block', borderBottom: '1px solid var(--secondary-color)', paddingBottom: '2px', fontSize: nextFractionSize, fontFamily, fontWeight }}>
                                {formatText(match[1], nextFractionSize)}
                            </span>
                            <span style={{ display: 'inline-block', marginTop: '2px', fontSize: nextFractionSize, fontFamily, fontWeight }}>
                                {formatText(match[2], nextFractionSize)}
                            </span>
                        </span>
                        {formatText(str.substring(match.index + match[0].length), currentFractionSize)}
                    </React.Fragment>
                );
            } else if (matchSym && (!matchSub || matchSym.index < matchSub.index) && (!matchSup || matchSym.index < matchSup.index) && (!matchFrac || matchSym.index < matchFrac.index) && (!matchChem || matchSym.index < matchChem.index) && (!matchBold || matchSym.index < matchBold.index) && (!matchItalic || matchSym.index < matchItalic.index) && (!matchSmall || matchSym.index < matchSmall.index) && (!matchBig || matchSym.index < matchBig.index)) {
                match = matchSym;
                let symbol = specialSymbols[match[1]];
                return (
                    <React.Fragment>
                        {formatText(str.substring(0, match.index), currentFractionSize)}
                        {symbol ? symbol : `{${match[1]}}`}
                        {formatText(str.substring(match.index + match[0].length), currentFractionSize)}
                    </React.Fragment>
                );
            } else if (matchChem && (!matchSub || matchChem.index < matchSub.index) && (!matchSup || matchChem.index < matchSup.index) && (!matchFrac || matchChem.index < matchFrac.index) && (!matchSym || matchChem.index < matchSym.index) && (!matchBold || matchChem.index < matchBold.index) && (!matchItalic || matchChem.index < matchItalic.index) && (!matchSmall || matchChem.index < matchSmall.index) && (!matchBig || matchChem.index < matchBig.index)) {
                match = matchChem;
                return (
                    <React.Fragment>
                        {formatText(str.substring(0, match.index), currentFractionSize)}
                        <span style={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'center', verticalAlign: 'middle' }}>
                            <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <sup>{match[3] || "\u00A0"}</sup>
                                <sub>{match[2] || "\u00A0"}</sub>
                            </span>
                            {match[1]}
                            <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                <sup>{match[4] || "\u00A0"}</sup>
                                <sub>{match[5] || "\u00A0"}</sub>
                            </span>
                        </span>
                        {formatText(str.substring(match.index + match[0].length), currentFractionSize)}
                    </React.Fragment>
                );
            } else if (matchBold && (!matchSub || matchBold.index < matchSub.index) && (!matchSup || matchBold.index < matchSup.index) && (!matchFrac || matchBold.index < matchFrac.index) && (!matchSym || matchBold.index < matchSym.index)) {
                match = matchBold;
                return (
                    <React.Fragment>
                        {formatText(str.substring(0, match.index), currentFractionSize)}
                        <b>{formatText(match[1], currentFractionSize)}</b>
                        {formatText(str.substring(match.index + match[0].length), currentFractionSize)}
                    </React.Fragment>
                );
            } else if (matchItalic && (!matchSub || matchItalic.index < matchSub.index) && (!matchSup || matchItalic.index < matchSup.index) && (!matchFrac || matchItalic.index < matchFrac.index) && (!matchSym || matchItalic.index < matchSym.index)) {
                match = matchItalic;
                return (
                    <React.Fragment>
                        {formatText(str.substring(0, match.index), currentFractionSize)}
                        <i>{formatText(match[1], currentFractionSize)}</i>
                        {formatText(str.substring(match.index + match[0].length), currentFractionSize)}
                    </React.Fragment>
                );
            } else if (matchSmall && (!matchSub || matchSmall.index < matchSub.index) && (!matchSup || matchSmall.index < matchSup.index) && (!matchFrac || matchSmall.index < matchFrac.index) && (!matchSym || matchSmall.index < matchSym.index)) {
                match = matchSmall;
                return (
                    <React.Fragment>
                        {formatText(str.substring(0, match.index), currentFractionSize)}
                        <span style={{ fontSize: '1.3rem' }}>{formatText(match[1], currentFractionSize)}</span>
                        {formatText(str.substring(match.index + match[0].length), currentFractionSize)}
                    </React.Fragment>
                );
            } else if (matchBig && (!matchSub || matchBig.index < matchSub.index) && (!matchSup || matchBig.index < matchSup.index) && (!matchFrac || matchBig.index < matchFrac.index) && (!matchSym || matchBig.index < matchSym.index)) {
                match = matchBig;
                return (
                    <React.Fragment>
                        {formatText(str.substring(0, match.index), currentFractionSize)}
                        <span style={{ fontSize: '1.3rem' }}>{formatText(match[1], currentFractionSize)}</span>
                        {formatText(str.substring(match.index + match[0].length), currentFractionSize)}
                    </React.Fragment>
                );
            } else if (matchNewLine && (!matchSub || matchNewLine.index < matchSub.index) && (!matchSup || matchNewLine.index < matchSup.index) && (!matchFrac || matchNewLine.index < matchFrac.index) && (!matchSym || matchNewLine.index < matchSym.index) && (!matchChem || matchNewLine.index < matchChem.index) && (!matchBold || matchNewLine.index < matchBold.index) && (!matchItalic || matchNewLine.index < matchItalic.index) && (!matchSmall || matchNewLine.index < matchSmall.index) && (!matchBig || matchNewLine.index < matchBig.index)) {
                match = matchNewLine;
                return (
                    <React.Fragment>
                        {formatText(str.substring(0, match.index), currentFractionSize)}
                        <br />
                        {formatText(str.substring(match.index + match[0].length), currentFractionSize)}
                    </React.Fragment>
                );
            }
        }


        return str;
    }

    return (
        <div style={{ maxWidth: "100%", overflowWrap: "break-word", wordBreak: "break-word", fontSize: "1.3rem", fontWeight: "400", fontFamily: "Inter", letterSpacing: "-0.6px" }}>
            {formatText(text, fractionSize)}
        </div>
    );
};

function getPaddedNumber(number) {
    return String(number).padStart(2, '0');
};


function escapeRegExp(string) {
    return string.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&');
};


export default function Home() {
    const { theme } = useContext(DataContext);
    const [randomPaper, setRandompaper] = useState(json[Math.floor(Math.random() * json.length)])
    const [demoQuestion, setDemoQuestion] = useState(randomPaper.questions[Math.floor(Math.random() * randomPaper.questions.length)]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [textInputs, setTextInputs] = useState([]);

    const [tts, setTts] = useState({
        "qid": "",
        "status": null,
    });

    let ttss = [];

    const Inputs = ({ question }) => {
        const textInput = textInputs.filter(item => item.id === question.qid)[0];
        const lines = Math.max(question?.mark_scheme?.max_marks, 2);

        if (question?.type === "written") {
            return (
                <div className={styles["answer-input-written"]} style={{ paddingLeft: "30px", paddingTop: "15px" }}>
                    {
                        Array.from({ length: lines }, (_, index) => (
                            <div key={index} className={styles["answer-input-written-line"]} style={{ top: `${index * 50}px` }}></div>
                        ))
                    }
                    <textarea
                        // disabled={((textInput && textInput.status) || (aiInUse)) ? true : false}
                        spellCheck={false}
                        rows={lines}
                        maxLength={2096}
                        id={`input-${question.qid}`}
                        ref={textInput ? textInput.ref : null}
                        style={{ height: `${lines * 50}px` }}
                    ></textarea>
                    <h3 className={styles.marksCount}>{question?.mark_scheme?.max_marks}</h3>
                    <button
                        className={styles["answer-input-submit"]}
                        onClick={(e) => {window.location.href = "/simulator"}}
                    >
                        Mark
                        <ArrowDown />
                    </button>
                </div>
            )
        } else if (question?.type === "multiple" && question.choices_type === "table") {
            return (
                <div className={styles.answerInputMultipleTable}>
                    {question?.mark_scheme.table.map((row, rowIndex) => {
                        return (
                            <div className={styles.tableRowContainer} onClick={(e) => { window.location.href = "/simulator" }}>
                                <h3 className={styles.tableRowOption}>{row.value.toUpperCase()}</h3>
                                <div className={styles.tableRow}>
                                    {row.values.map((value, index) => {
                                        const style1 = index == row.values.length - 1 ? {} : { borderRight: "none" }
                                        const style2 = rowIndex == question?.mark_scheme.table.length - 1 ? {} : { borderBottom: "none" }
                                        const style = { ...style1, ...style2 };
                                        return (
                                            <h3 className={styles.rowValue} style={style}>{value.value}</h3>
                                        )
                                    })}
                                </div>
                                <h3 onClick={(e) => { window.location.ref = "/simulator" }} className={`${styles.tableRowCheckBox} ${textInput?.data === row.value ? styles.tableRowCheckBoxSelected : ""}`}>
                                    <Cross styling={{ width: "75%", height: "75%", fill: "var(--primary-color)" }} />
                                </h3>
                            </div>
                        )
                    })}
                    <h3 className={styles.marksCount}>{question?.mark_scheme?.max_marks}</h3>
                    <button
                        className={styles["answer-input-submit"]}
                        onClick={(e) => {
                            window.location.href = "/simulator"
                        }}
                    >
                        Mark
                        <ArrowDown />
                    </button>
                </div>
            )
        } else if (question?.type === "multiple" && question.choices_type === "text") {
            return (
                <div className={styles.answerInputMultipleTable}>
                    {question?.mark_scheme?.choices?.map((choice, choiceIndex) => {
                        return (
                            <div className={styles.multipleChoiceContainer} style={images[`./${choice.text}.png`] || images[`./${choice.text}.jpg`] ? { height: "auto" } : {}} onClick={(e) => { window.location.href = "/simulator" }}>
                                <h3 className={styles.tableRowOption}>{getLetterFromNumber(choiceIndex).toUpperCase()}</h3>
                                <h3 className={styles.multipleChoiceText}>
                                    {
                                        images[`./${choice.text}.png`] || images[`./${choice.text}.jpg`] ?
                                            <img className={`${styles.imageDisplayTable} ${theme === "dark" ? styles.darkModeImage : ""}`} src={images[`./${theme === "dark" ? "dark_" : ""}${choice.text}.jpg`] || images[`./${theme === "dark" ? "dark_" : ""}${choice.text}.png`] || "https://placehold.co/600x400"}></img>
                                            :
                                            <LatexFormatter text={choice.text} />
                                    }
                                </h3>
                                <h3 style={textInput?.data === getLetterFromNumber(choiceIndex) ? { backgroundColor: "var(--buttonBgColor)", outline: "1px solid rgba(0,80,255,0.5)", border: "none" } : {}} onClick={(e) => {window.location.href = "/simulator"}} className={`${styles.tableRowCheckBox} ${textInput?.data === getLetterFromNumber(choiceIndex) ? styles.tableRowCheckBoxSelected : ""}`}>
                                    <Cross />
                                </h3>
                            </div>
                        )
                    })}
                    <h3 className={styles.marksCount}>{question?.mark_scheme?.max_marks}</h3>
                    <button
                        className={styles["answer-input-submit"]}
                        onClick={(e) => {
                            window.location.href = "/simulator"
                        }}
                    >
                        Mark
                        <ArrowDown />
                    </button>
                </div>
            )
        } else if (question?.type === "multiple" && question.choices_type === "image") {
            return (
                <div className={styles.answerInputMultipleTable}>
                    {question?.mark_scheme?.choices?.map((choice, choiceIndex) => {
                        return (
                            <div className={styles.multipleChoiceImageContainer} onClick={(e) => { window.location.href = "/simulator" }}>
                                <h3 className={styles.tableRowOption}>{getLetterFromNumber(choiceIndex).toUpperCase()}</h3>
                                <div className={`${styles.diagram}`} style={{ justifyContent: "flex-start" }}>
                                    <img className={`${styles.imageDisplayTable} ${theme === "dark" ? styles.darkModeImage : ""}`} src={images[`./${theme === "dark" ? "dark_" : ""}${choice.path}.jpg`] || images[`./${theme === "dark" ? "dark_" : ""}${choice.path}.png`] || "https://placehold.co/600x400"}></img>
                                </div>
                                <h3 style={textInput?.data === getLetterFromNumber(choiceIndex) ? { backgroundColor: "var(--buttonBgColor)", outline: "1px solid rgba(0,80,255,0.5)", border: "none" } : {}} onClick={(e) => {window.location.href = "/simulator"}} className={`${styles.tableRowCheckBox} ${textInput?.data === getLetterFromNumber(choiceIndex) ? styles.tableRowCheckBoxSelected : ""}`}>
                                    <Cross />
                                </h3>
                            </div>
                        )
                    })}
                    <h3 className={styles.marksCount}>{question?.mark_scheme?.max_marks}</h3>
                    <button
                        className={styles["answer-input-submit"]}
                        onClick={(e) => {
                            window.location.href = "/simulator"
                        }}
                    >
                        Mark
                        <ArrowDown />
                    </button>
                </div>
            )
        }
    }

    useEffect(() => {
        let intervalId;

        // Function to increment the current index and update the demo question
        const updateDemoQuestion = () => {
            // setCurrentIndex((prevIndex) => (prevIndex + 1) % json[0].questions.length);
            const newPaper = json[Math.floor(Math.random() * json.length)];

            setDemoQuestion(newPaper.questions[Math.floor(Math.random() * newPaper.questions.length)])
        };

        // Start the timer on mount
        intervalId = setInterval(updateDemoQuestion, 4000);

        // Clean up the timer when the component unmounts
        return () => {
            clearInterval(intervalId);
        };
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add(`${styles.show}`);
                } else {
                    entry.target.classList.remove(`${styles.show}`);
                }
            })
        });

        const hiddenElements = document.querySelectorAll(".hidden");
        hiddenElements.forEach((el) => observer.observe(el))
    }, [])

    const AQAStructure = ({ structure, information, index, length }) => {
        switch (structure.type) {
            case "description":
                console.log(structure)
                if (structure?.display_text?.length == 0) {
                    return
                }

                const style1 = structure.center ? { display: "flex", justifyContent: "center", alignItems: "center" } : {};
                const style2 = structure.style;
                const style3 = { paddingLeft: `${(structure.indent + 1) * 0}px` }
                const style4 = structure.bullet_point && index != length - 1 ? { marginBottom: "0" } : {}

                console.log(index);
                const style5 = index == length - 1 ? { marginBottom: "40px" } : {}

                const style = { ...style1, ...style2, ...style3, ...style4, ...style5 }

                const text = structure.display_text;

                console.log(text)


                return (
                    <div className={`${styles["description-structure"]} ${structure.bullet_point && styles.bullet_point}`} style={style} >
                        {/* <ItemString styling={{ paddingBlock: "1px", display: "flex" }} string={structure.display_text || structure.content} /> */}
                        <LatexFormatter text={text} />
                    </div>
                );
            case "image":
                return (
                    <div className={styles["diagram"]}>
                        {(images[`./${structure.path}.jpg`] || images[`./${structure.path}.png`])
                            ?
                            <img className={theme === "dark" ? styles.darkModeImage : ""} src={images[`./${theme === "dark" ? "dark_" : ""}${structure.path}.jpg`] || images[`./${theme === "dark" ? "dark_" : ""}${structure.path}.png`]} style={{ marginBottom: "20px" }} />
                            :
                            <div style={{ width: "100%", height: "100px", backgroundColor: "#000", color: "#fff" }}>ERROR NO IMAGE</div>
                        }
                        {/* <img src={images[`./graph.png`]}></img> */}
                        {/* <img src={images[`./${structure.path}.png`]}></img> */}
                        {/* <img src={images[`./${structure.path}.png`]}></img> */}
                    </div>
                );
        }

        return (
            <div>{structure.type}</div>
        )
    };

    const AQANumber = ({ qid1, qid_relative, depth }) => {
        return (
            <div className={styles.aqaBoxContainer}>
                {getPaddedNumber(qid1).split("").map((letter, index) => {
                    return (
                        <div className={styles.aqaBox} style={index === 0 ? { borderTopLeftRadius: "6px", borderBottomLeftRadius: "6px" } : { borderTopRightRadius: "6px", borderBottomRightRadius: "6px" }}>{letter}</div>
                    )
                })}
                {depth > 1
                    &&
                    <>
                        <div className={styles.aqaDot}>.</div>
                        <div className={styles.aqaBox} style={{ borderRadius: "6px" }}>{qid_relative}</div>
                    </>
                }
            </div>
        )
    };

    const examBoard = "AQA";

    const Question = ({ question, maxDepth, depth, qid1, qid_relative, parent, paper_information }) => {
        const paddedLeftBar = examBoard !== "AQA";
        const info = paper_information

        if (depth > maxDepth) {
            return;
        };

        const textInput = textInputs.filter(item => item.id === question.qid)[0];

        const style1 = paddedLeftBar ? depth === 1 ? {} : {} : { position: "absolute", left: 0 }
        const style2 = { borderLeft: textInput && textInput.status === "correct" ? "6px solid rgba(0,255,80,1)" : textInput && textInput.status === "incorrect" ? "6px solid rgba(255,0,80,1)" : textInput && textInput.status === "partial" ? "6px solid rgba(255,150,80,1)" : "transparent", backgroundColor: textInput && textInput.status === "correct" ? "rgba(0,255,80,0.75)" : textInput && textInput.status === "incorrect" ? "rgba(255,0,80,0.75)" : textInput && textInput.status === "partial" ? "rgba(255,150,80,0.75)" : "transparent", transform: "translateX(-2px)" }
        const style = (paddedLeftBar) ? { ...style2, marginLeft: "10px", paddingRight: "20px" } : depth === 1 ? parent.questions.length === 1 ? { marginLeft: "70px" } : { marginLeft: "115px" } : { ...style2, paddingRight: "20px" };

        const style3 = depth === 1 ? { position: "relative", marginBottom: 0 } : {};
        const style4 = depth > 1 ? { display: "flex" } : {};

        const style5 = parent.content.length < 2 ? { display: "flex" } : {}

        const leftBarStyle = { ...style1, ...style5 }

        const micHandler = async () => {
            setTts(prev => {
                if (prev.qid !== question?.qid) {
                    prev.status = null;
                };

                prev.qid = question?.qid;
                if (!prev.status) {
                    prev.status = "active";
                } else if (prev.status === "active") {
                    prev.status = "paused";
                } else if (prev.status === "paused") {
                    prev.status = "active";
                };

                return { ...prev };
            });
        };

        const micClass = styles[`mic${tts.status}`];
        const micComponent = !tts.status ? <Mic onClick={micHandler} className={micClass} /> : tts.status === "active" ? <Pause onClick={micHandler} className={micClass} /> : <Play onClick={micHandler} className={micClass} />;
        const hasMic = ttss.includes(question.qid);

        if (!hasMic) {
            ttss = [...ttss, question.qid];
        };

        return (
            <div className={styles.questionContainer} style={{ ...style3, ...style4 }}>
                <div className={styles.questionQuestionContainer}>
                    {depth === 1 ?
                        <div className={styles.questionLeftBar} style={leftBarStyle}>
                            <AQANumber qid1={qid1} qid_relative={question.qid_relative} depth={depth} />
                            {/* <Mic /> */}
                        </div>
                        :
                        depth === maxDepth && parent.questions.length > 1 ?
                            <div className={styles.questionLeftBar} style={style1}>
                                <AQANumber qid1={qid1} qid_relative={question.qid_relative} depth={depth} />
                                {/* <Mic /> */}
                            </div>
                            :
                            depth === maxDepth - 1 && parent.questions.length > 1 &&
                            <div className={styles.questionLeftBar} style={style1}>
                                <AQANumber qid1={qid1} qid_relative={question.qid_relative} depth={depth} />
                                {/* <Mic /> */}
                            </div>
                    }

                    <div className={styles.questionContent} style={{ ...style }}>
                        {!hasMic && depth > 1 ?
                            question?.qid === tts?.qid
                                ?
                                micComponent
                                :
                                <Mic onClick={micHandler} />
                            :
                            ""
                        }

                        {question?.content && question.content.map((structure, index) => {
                            if (examBoard === "AQA") {
                                return (
                                    <AQAStructure structure={structure} information={question} />
                                )
                            }
                        })}

                        {
                            depth === maxDepth && examBoard !== "Edexcel" && <Inputs question={question} />
                        }


                        {question?.questions && question.questions.map((question2, index) => {
                            return (
                                <Question question={question2} maxDepth={maxDepth} depth={depth + 1} qid1={qid1} parent={depth + 1 < maxDepth ? question : parent} />
                            )
                        })}
                    </div>
                </div>
                {depth === 1 &&
                    <div className={styles.sourceContainer}>
                        <a>{info.paper_date_year}</a>
                        <a>{info.paper_level_abr}</a>
                        {/* <a>{papers.filter(paper => paper.paper_information.paper_id === currentSet[0].paper_id)[0].paper_information.paper_level_full}</a> */}
                        <a>{info.paper_sub_name.split(":")[0].charAt(0).toUpperCase() + info.paper_sub_name.split(":")[0].slice(1).toLowerCase()}</a>
                        <a href={info.paper_question_url} target="_blank">Question Paper Link</a>
                        <a href={info.paper_mark_scheme_url} target="_blank">Mark Scheme Link</a>
                    </div>
                }
            </div>
        )
    };

    return (
        <div className={styles["page-container"]}>
            <div className={`${styles.page}`}>
                <div className={styles.pageContent}>
                    <div className={styles.titleText}>
                        <h1 className={styles.title}>
                            <span className={styles.titlePart} style={{ paddingLeft: "0px" }}>The</span>
                            <span className={`${styles.titlePart} ${styles.gradient}`} style={{ paddingLeft: "10px" }}>modern</span>
                            <span className={styles.titlePart} style={{ paddingLeft: "10px" }}>way</span>
                            <span className={styles.titlePart} style={{ paddingLeft: "10px" }}>to</span>
                            <span className={styles.titlePart} style={{ paddingLeft: "10px" }}>practise</span>
                            <span className={styles.titlePart} style={{ paddingLeft: "0px" }}>A</span>
                            <span className={styles.titlePart} style={{ paddingLeft: "10px" }}>Levels</span>
                            <span className={styles.titlePart} style={{ paddingLeft: "10px" }}>and</span>
                            <span className={styles.titlePart} style={{ paddingLeft: "10px" }}>GCSEs</span>
                        </h1>
                        <h1 className={styles.heading}>Interactive past papers with self-marking, instant feedback and full customizability</h1>
                        <div className={styles.bulletPoints}>
                            <div className={styles.bulletPoint}>
                                <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 448 512"><path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z" /></svg>
                                <h3>Completely Free</h3>
                            </div>
                            <div className={styles.bulletPoint}>
                                <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 448 512"><path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z" /></svg>
                                <h3>No Login</h3>
                            </div>
                            <div className={styles.bulletPoint}>
                                <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 448 512"><path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z" /></svg>
                                <h3>Daily Updates</h3>
                            </div>
                        </div>
                        <button onClick={(e) => { window.location.href = "/simulator" }} className={styles.ctaButton}>
                            Get Started
                            <ArrowDown />
                        </button>
                    </div>
                    <div className={styles.graphicContainerContainer}>
                        <div className={styles.graphic}>
                            <div className={styles.graphicCover}></div>
                            <div className={styles.graphicContainer}>
                                <Question question={demoQuestion} maxDepth={2} depth={1} qid1={demoQuestion.qid_relative} parent={demoQuestion} paper_information={json[0].paper_information} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={`${styles.page} ${styles.contrast} ${styles.glowingTop} ${styles.ellipse}`} style={{ paddingBlock: "40px", marginBottom: "0", borderTop: "2px solid var(--underLine)" }}>
                <div className={styles.pageContent} style={{ flexDirection: "column", justifyContent: "flex-start" }}>
                    <h1 className={`${styles.pageTitle}`}>Features</h1>
                    <div className={styles.featureList}>
                        <div className={`${styles.feature} ${styles.hidden} hidden`}>
                            <div className={styles.featureBackground}>

                            </div>
                            <div className={styles.featureContent}>
                                <h3 className={styles.featureTitle}>
                                    <svg style={{ marginRight: "10px", marginTop: "4px" }} xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512"><path style={{ fill: "var(--secondary-color)" }} d="M464 6.1c9.5-8.5 24-8.1 33 .9l8 8c9 9 9.4 23.5 .9 33l-85.8 95.9c-2.6 2.9-4.1 6.7-4.1 10.7V176c0 8.8-7.2 16-16 16H384.2c-4.6 0-8.9 1.9-11.9 5.3L100.7 500.9C94.3 508 85.3 512 75.8 512c-8.8 0-17.3-3.5-23.5-9.8L9.7 459.7C3.5 453.4 0 445 0 436.2c0-9.5 4-18.5 11.1-24.8l111.6-99.8c3.4-3 5.3-7.4 5.3-11.9V272c0-8.8 7.2-16 16-16h34.6c3.9 0 7.7-1.5 10.7-4.1L464 6.1zM432 288c3.6 0 6.7 2.4 7.7 5.8l14.8 51.7 51.7 14.8c3.4 1 5.8 4.1 5.8 7.7s-2.4 6.7-5.8 7.7l-51.7 14.8-14.8 51.7c-1 3.4-4.1 5.8-7.7 5.8s-6.7-2.4-7.7-5.8l-14.8-51.7-51.7-14.8c-3.4-1-5.8-4.1-5.8-7.7s2.4-6.7 5.8-7.7l51.7-14.8 14.8-51.7c1-3.4 4.1-5.8 7.7-5.8zM87.7 69.8l14.8 51.7 51.7 14.8c3.4 1 5.8 4.1 5.8 7.7s-2.4 6.7-5.8 7.7l-51.7 14.8L87.7 218.2c-1 3.4-4.1 5.8-7.7 5.8s-6.7-2.4-7.7-5.8L57.5 166.5 5.8 151.7c-3.4-1-5.8-4.1-5.8-7.7s2.4-6.7 5.8-7.7l51.7-14.8L72.3 69.8c1-3.4 4.1-5.8 7.7-5.8s6.7 2.4 7.7 5.8zM208 0c3.7 0 6.9 2.5 7.8 6.1l6.8 27.3 27.3 6.8c3.6 .9 6.1 4.1 6.1 7.8s-2.5 6.9-6.1 7.8l-27.3 6.8-6.8 27.3c-.9 3.6-4.1 6.1-7.8 6.1s-6.9-2.5-7.8-6.1l-6.8-27.3-27.3-6.8c-3.6-.9-6.1-4.1-6.1-7.8s2.5-6.9 6.1-7.8l27.3-6.8 6.8-27.3c.9-3.6 4.1-6.1 7.8-6.1z" /></svg>
                                    Automatic Marking
                                </h3>
                                <h4 className={styles.featureDescription}>
                                    {/* Experience the convenience of immediate grading for multiple choice questions, and look forward to the advanced capabilities of AI-assisted marking for written responses in the near future. */}
                                    Automatic marking for multiple choice questions, and intuitive self-marking system for written questions with instant feedback. Soon to be AI-assisted.
                                </h4>
                            </div>
                        </div>
                        <div className={`${styles.feature} ${styles.hidden} hidden`}>
                            <div className={styles.featureBackground}></div>
                            <div className={styles.featureContent}>
                                <h3 className={styles.featureTitle}>
                                    <svg style={{ marginRight: "10px", marginTop: "4px" }} xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512"><path style={{ fill: "var(--secondary-color)" }} d="M0 96C0 78.3 14.3 64 32 64H416c17.7 0 32 14.3 32 32s-14.3 32-32 32H32C14.3 128 0 113.7 0 96zM64 256c0-17.7 14.3-32 32-32H480c17.7 0 32 14.3 32 32s-14.3 32-32 32H96c-17.7 0-32-14.3-32-32zM448 416c0 17.7-14.3 32-32 32H32c-17.7 0-32-14.3-32-32s14.3-32 32-32H416c17.7 0 32 14.3 32 32z" /></svg>
                                    Study Flow
                                </h3>
                                <h4 className={styles.featureDescription}>
                                    No more head turning and tab switching from paper to mark scheme, After every question, the relevant mark scheme is instantly presented - streamlining your study, eliminating distraction, and enhancing productivity.
                                </h4>
                            </div>
                        </div>
                        <div className={`${styles.feature} ${styles.hidden} hidden`}>
                            <div className={styles.featureBackground}>

                            </div>
                            <div className={styles.featureContent}>
                                <h3 className={styles.featureTitle}>
                                    <svg style={{ marginRight: "10px", marginTop: "3px" }} xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 640 512"><path style={{ fill: "var(--secondary-color)" }} d="M308.5 135.3c7.1-6.3 9.9-16.2 6.2-25c-2.3-5.3-4.8-10.5-7.6-15.5L304 89.4c-3-5-6.3-9.9-9.8-14.6c-5.7-7.6-15.7-10.1-24.7-7.1l-28.2 9.3c-10.7-8.8-23-16-36.2-20.9L199 27.1c-1.9-9.3-9.1-16.7-18.5-17.8C173.9 8.4 167.2 8 160.4 8h-.7c-6.8 0-13.5 .4-20.1 1.2c-9.4 1.1-16.6 8.6-18.5 17.8L115 56.1c-13.3 5-25.5 12.1-36.2 20.9L50.5 67.8c-9-3-19-.5-24.7 7.1c-3.5 4.7-6.8 9.6-9.9 14.6l-3 5.3c-2.8 5-5.3 10.2-7.6 15.6c-3.7 8.7-.9 18.6 6.2 25l22.2 19.8C32.6 161.9 32 168.9 32 176s.6 14.1 1.7 20.9L11.5 216.7c-7.1 6.3-9.9 16.2-6.2 25c2.3 5.3 4.8 10.5 7.6 15.6l3 5.2c3 5.1 6.3 9.9 9.9 14.6c5.7 7.6 15.7 10.1 24.7 7.1l28.2-9.3c10.7 8.8 23 16 36.2 20.9l6.1 29.1c1.9 9.3 9.1 16.7 18.5 17.8c6.7 .8 13.5 1.2 20.4 1.2s13.7-.4 20.4-1.2c9.4-1.1 16.6-8.6 18.5-17.8l6.1-29.1c13.3-5 25.5-12.1 36.2-20.9l28.2 9.3c9 3 19 .5 24.7-7.1c3.5-4.7 6.8-9.5 9.8-14.6l3.1-5.4c2.8-5 5.3-10.2 7.6-15.5c3.7-8.7 .9-18.6-6.2-25l-22.2-19.8c1.1-6.8 1.7-13.8 1.7-20.9s-.6-14.1-1.7-20.9l22.2-19.8zM112 176a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zM504.7 500.5c6.3 7.1 16.2 9.9 25 6.2c5.3-2.3 10.5-4.8 15.5-7.6l5.4-3.1c5-3 9.9-6.3 14.6-9.8c7.6-5.7 10.1-15.7 7.1-24.7l-9.3-28.2c8.8-10.7 16-23 20.9-36.2l29.1-6.1c9.3-1.9 16.7-9.1 17.8-18.5c.8-6.7 1.2-13.5 1.2-20.4s-.4-13.7-1.2-20.4c-1.1-9.4-8.6-16.6-17.8-18.5L583.9 307c-5-13.3-12.1-25.5-20.9-36.2l9.3-28.2c3-9 .5-19-7.1-24.7c-4.7-3.5-9.6-6.8-14.6-9.9l-5.3-3c-5-2.8-10.2-5.3-15.6-7.6c-8.7-3.7-18.6-.9-25 6.2l-19.8 22.2c-6.8-1.1-13.8-1.7-20.9-1.7s-14.1 .6-20.9 1.7l-19.8-22.2c-6.3-7.1-16.2-9.9-25-6.2c-5.3 2.3-10.5 4.8-15.6 7.6l-5.2 3c-5.1 3-9.9 6.3-14.6 9.9c-7.6 5.7-10.1 15.7-7.1 24.7l9.3 28.2c-8.8 10.7-16 23-20.9 36.2L315.1 313c-9.3 1.9-16.7 9.1-17.8 18.5c-.8 6.7-1.2 13.5-1.2 20.4s.4 13.7 1.2 20.4c1.1 9.4 8.6 16.6 17.8 18.5l29.1 6.1c5 13.3 12.1 25.5 20.9 36.2l-9.3 28.2c-3 9-.5 19 7.1 24.7c4.7 3.5 9.5 6.8 14.6 9.8l5.4 3.1c5 2.8 10.2 5.3 15.5 7.6c8.7 3.7 18.6 .9 25-6.2l19.8-22.2c6.8 1.1 13.8 1.7 20.9 1.7s14.1-.6 20.9-1.7l19.8 22.2zM464 304a48 48 0 1 1 0 96 48 48 0 1 1 0-96z" /></svg>
                                    Paper Customizability
                                </h3>
                                <h4 className={styles.featureDescription}>
                                    Transition effortlessly between papers or mix-and-match your selection. With just a couple of clicks, our intuitive filter lets you customize your practice material in real-time, adapting to your study needs.
                                </h4>
                            </div>
                        </div>
                        <div className={`${styles.feature} ${styles.hidden} hidden`}>
                            <div className={styles.featureBackground}>

                            </div>
                            <div className={styles.featureContent}>
                                <h3 className={styles.featureTitle}>
                                    <svg style={{ marginRight: "10px", marginTop: "4px" }} xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512"><path style={{ fill: "var(--secondary-color)" }} d="M512 256c0 .9 0 1.8 0 2.7c-.4 36.5-33.6 61.3-70.1 61.3H344c-26.5 0-48 21.5-48 48c0 3.4 .4 6.7 1 9.9c2.1 10.2 6.5 20 10.8 29.9c6.1 13.8 12.1 27.5 12.1 42c0 31.8-21.6 60.7-53.4 62c-3.5 .1-7 .2-10.6 .2C114.6 512 0 397.4 0 256S114.6 0 256 0S512 114.6 512 256zM128 288a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm0-96a32 32 0 1 0 0-64 32 32 0 1 0 0 64zM288 96a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm96 96a32 32 0 1 0 0-64 32 32 0 1 0 0 64z" /></svg>
                                    Immersive Interface
                                </h3>
                                <h4 className={styles.featureDescription}>
                                    {/* Enjoy a clutter-free, minimalist UI designed for maximum focus. Comes with a soothing dark mode for those long night sessions, crafting an immersive study environment tailored to your comfort. */}
                                    Minimalist, clutter-free UI designed around minimsing distractions and maximising user satisfaction to make doing past papers more enjoyable.
                                </h4>
                            </div>
                        </div>
                        <div className={`${styles.feature} ${styles.hidden} hidden`}>
                            <div className={styles.featureBackground}></div>
                            <div className={styles.featureContent}>
                                <h3 className={styles.featureTitle}>
                                    <svg style={{ marginRight: "10px", marginTop: "4px" }} xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512">
                                        <path style={{ fill: "var(--secondary-color)" }} d="M352 256c0 22.2-1.2 43.6-3.3 64H163.3c-2.2-20.4-3.3-41.8-3.3-64s1.2-43.6 3.3-64H348.7c2.2 20.4 3.3 41.8 3.3 64zm28.8-64H503.9c5.3 20.5 8.1 41.9 8.1 64s-2.8 43.5-8.1 64H380.8c2.1-20.6 3.2-42 3.2-64s-1.1-43.4-3.2-64zm112.6-32H376.7c-10-63.9-29.8-117.4-55.3-151.6c78.3 20.7 142 77.5 171.9 151.6zm-149.1 0H167.7c6.1-36.4 15.5-68.6 27-94.7c10.5-23.6 22.2-40.7 33.5-51.5C239.4 3.2 248.7 0 256 0s16.6 3.2 27.8 13.8c11.3 10.8 23 27.9 33.5 51.5c11.6 26 20.9 58.2 27 94.7zm-209 0H18.6C48.6 85.9 112.2 29.1 190.6 8.4C165.1 42.6 145.3 96.1 135.3 160zM8.1 192H131.2c-2.1 20.6-3.2 42-3.2 64s1.1 43.4 3.2 64H8.1C2.8 299.5 0 278.1 0 256s2.8-43.5 8.1-64zM194.7 446.6c-11.6-26-20.9-58.2-27-94.6H344.3c-6.1 36.4-15.5 68.6-27 94.6c-10.5 23.6-22.2 40.7-33.5 51.5C272.6 508.8 263.3 512 256 512s-16.6-3.2-27.8-13.8c-11.3-10.8-23-27.9-33.5-51.5zM135.3 352c10 63.9 29.8 117.4 55.3 151.6C112.2 482.9 48.6 426.1 18.6 352H135.3zm358.1 0c-30 74.1-93.6 130.9-171.9 151.6c25.5-34.2 45.2-87.7 55.3-151.6H493.4z" />
                                    </svg>
                                    Accessiblity
                                </h3>
                                <h4 className={styles.featureDescription}>
                                    Bringing you past papers anywhere and everywhere, gone are the days of manually printing past papers and mark schemes
                                </h4>
                            </div>
                        </div>
                        <div className={`${styles.feature} ${styles.hidden} hidden`}>
                            <div className={styles.featureBackground}></div>
                            <div className={styles.featureContent}>
                                <h3 className={styles.featureTitle}>
                                    <svg style={{ marginRight: "10px", marginTop: "4px" }} xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 448 512"><path style={{ fill: "var(--secondary-color)" }} d="M246.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-160 160c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L224 109.3 361.4 246.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-160-160zm160 352l-160-160c-12.5-12.5-32.8-12.5-45.3 0l-160 160c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L224 301.3 361.4 438.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3z" /></svg>
                                    Constantly Improving
                                </h3>
                                <h4 className={styles.featureDescription}>
                                    Our goal is to make the most efficient tool for A Levels and GCSE revision, every single detail, every single possible feature that will improve your experience, your satisfaction and ultimately your grades even if by 1%.
                                </h4>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <footer>

            </footer>
        </div>
    )
}    
